import { useDropdown } from "./use-dropdown";
import { useCallback, useMemo } from "react";
import { useScreenState } from "./use-screen-state";
import { useItemsDropdown } from "../components/Dropdown";
import { StyleProp, ViewStyle } from "react-native";
import { IconName } from "../components/Icon";

export type ScreenHeaderFilterItem<T> = {
    icon?: IconName;
    color?: string;
    label: string;
    selected?: boolean;
    style?: StyleProp<ViewStyle>;
    testID?: string;
    multiline?: boolean;
    value: T;
    quantity?: number;
};

type FilterType<T> = {
    value: T;
    label: string;
    quantity?: number;
};

type ScreenHeaderFilterProps<T> = {
    items: ScreenHeaderFilterItem<T>[];
    uniqueSelection?: boolean;
    defaultActiveFiltersValues?: T[];
};

export const useScreenHeaderFilters = <T extends string>({
    items = [],
    uniqueSelection,
    defaultActiveFiltersValues = [],
}: ScreenHeaderFilterProps<T>) => {
    const defaultOptions = useMemo(
        () => items.filter((item) => defaultActiveFiltersValues.includes(item.value)),
        [items, defaultActiveFiltersValues],
    );
    const [activeFilters, setActiveFilters] = useScreenState<FilterType<T>[]>(
        "filters",
        // By default, all items that are in the defaultActiveFilters values list are active
        defaultOptions,
    );

    const _setActiveFilters = useCallback(
        (item: FilterType<T>) => {
            if (uniqueSelection) {
                setActiveFilters((prev) =>
                    prev.find((e) => e.value === item.value) ? [] : [item],
                );
            } else {
                setActiveFilters((prev) => {
                    if (prev.find((e) => e.value === item.value)) {
                        // Keep at least one filter on (BAS-2875): unchecking the last one would
                        // leave the screen on its empty state, which reads as an error rather
                        // than as an over-narrowed filter.
                        if (prev.length === 1) {
                            return prev;
                        }

                        return prev.filter((e) => e.value !== item.value);
                    }

                    return [...prev, item];
                });
            }
        },
        [setActiveFilters, uniqueSelection],
    );

    const dropdownItems = [
        ...items.map((item) => {
            const selected = !!activeFilters.find((e) => e.value === item.value);
            return {
                ...item,
                selected,
                onClick: () => {
                    _setActiveFilters({ ...item });
                },
            };
        }),
    ];

    const dropdownElement = useItemsDropdown({
        uniqueSelection,
        items: dropdownItems,
        keepOpen: true,
    });

    const screenFilterDropdownProps = useDropdown({
        component: dropdownElement,
    });

    const onRemoveFilter = useCallback(
        (filterToRemove: string) => {
            setActiveFilters((prev) => {
                return prev.filter((filter) => filter.value !== filterToRemove);
            });
        },
        [setActiveFilters],
    );

    return {
        screenFilterDropdownProps,
        activeFilters,
        onRemoveFilter,
        hasCustomFilters:
            activeFilters.length !== defaultOptions.length ||
            activeFilters.some((f) => !defaultOptions.find((d) => d.value === f.value)),
    };
};
